import type { Candle, OverlaySettings } from '../types';

export type CandleHelperSignalType = 'bullishEngulfing' | 'bearishEngulfing' | 'doji';

export interface CandleHelperSignal {
  index: number;
  type: CandleHelperSignalType;
}

function isBullishEngulfing(previous: Candle, current: Candle): boolean {
  if (previous.close >= previous.open || current.close <= current.open) {
    return false;
  }

  return current.open <= previous.close && current.close >= previous.open;
}

function isBearishEngulfing(previous: Candle, current: Candle): boolean {
  if (previous.close <= previous.open || current.close >= current.open) {
    return false;
  }

  return current.open >= previous.close && current.close <= previous.open;
}

function isDoji(candle: Candle, bodyRatioPercent: number): boolean {
  const range = candle.high - candle.low;
  if (!Number.isFinite(range) || range <= 0) {
    return false;
  }

  const body = Math.abs(candle.close - candle.open);
  const ratio = body / range;
  return ratio <= bodyRatioPercent / 100;
}

export function computeCandleHelperSignals(
  candles: Candle[],
  inputs: OverlaySettings['candleHelper']['inputs'],
): CandleHelperSignal[] {
  if (candles.length < 2) {
    return [];
  }

  const signals: CandleHelperSignal[] = [];

  for (let index = 1; index < candles.length; index += 1) {
    const previous = candles[index - 1];
    const current = candles[index];

    if (inputs.detectBullishEngulfing && isBullishEngulfing(previous, current)) {
      signals.push({ index, type: 'bullishEngulfing' });
      continue;
    }

    if (inputs.detectBearishEngulfing && isBearishEngulfing(previous, current)) {
      signals.push({ index, type: 'bearishEngulfing' });
      continue;
    }

    if (inputs.detectDoji && isDoji(current, inputs.dojiBodyRatioPercent)) {
      signals.push({ index, type: 'doji' });
    }
  }

  return signals;
}
